import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { mktMe, mktLogin, mktVerifyOtp, mktLogout, type MktUser } from './marketplaceApi';
import { setDraftScope, clearDraft } from './wizardDraft';

/**
 * Auth state for the breakfast marketplace — deliberately separate from the main
 * platform `AuthContext`. It talks only to `/api/marketplace/auth/*`, so a
 * main dev.blue-pill.ai (account_type='platform') session is treated as logged
 * out here (and vice-versa).
 */
interface MarketplaceAuthValue {
  user: MktUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (
    email: string,
    password: string,
  ) => Promise<{ success: boolean; message: string; requiresVerification?: boolean }>;
  verifyOtp: (email: string, otpCode: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<MarketplaceAuthValue | undefined>(undefined);

const msg = (data: any, fallback: string) => {
  const d = data?.detail ?? data?.message;
  return typeof d === 'string' ? d : fallback;
};

export const MarketplaceAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<MktUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Keep the wizard-draft namespace pinned to the active user so a draft (which
  // carries the studyId + typed inputs) can never cross accounts in a shared tab.
  // Set synchronously — not in an effect — so any child reading the draft during
  // its own first render already sees the correct per-user scope.
  setDraftScope(user?.id ?? null);

  const refresh = async () => {
    try {
      setUser(await mktMe());
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, []);

  const login: MarketplaceAuthValue['login'] = async (email, password) => {
    const { ok, data } = await mktLogin(email, password);
    if (data?.requires_verification) {
      return {
        success: false,
        message: msg(data, 'Please verify your email.'),
        requiresVerification: true,
      };
    }
    if (ok && data?.user) {
      setUser(data.user);
      return { success: true, message: msg(data, 'Logged in') };
    }
    return { success: false, message: msg(data, 'Login failed') };
  };

  const verifyOtp: MarketplaceAuthValue['verifyOtp'] = async (email, otpCode) => {
    const { ok, data } = await mktVerifyOtp(email, otpCode);
    if (ok && data?.user) {
      setUser(data.user);
      return { success: true, message: msg(data, 'Verified') };
    }
    return { success: false, message: msg(data, 'Verification failed') };
  };

  const logout = async () => {
    // Drop this user's wizard draft while the scope still points at them (before
    // setUser(null) resets it), so nothing of theirs lingers for the next account.
    clearDraft();
    // Never let a failed server logout reject (callers navigate right after) —
    // clear the client session regardless so the UI can't get stuck signed in.
    try {
      await mktLogout();
    } catch {
      /* server logout failed; clear client state anyway */
    } finally {
      setUser(null);
    }
  };

  return (
    <Ctx.Provider
      value={{ user, isAuthenticated: !!user, loading, login, verifyOtp, logout, refresh }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useMarketplaceAuth = (): MarketplaceAuthValue => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMarketplaceAuth must be used within MarketplaceAuthProvider');
  return ctx;
};
